import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from "@angular/platform-browser";
import { NavigationEnd, Router } from "@angular/router";
// import { MatomoTracker } from 'ngx-matomo-client';
import { combineLatest, filter, startWith, Subject, switchMap, takeUntil, tap } from "rxjs";
import {  map } from "rxjs/operators";

import { AuthenticationService } from "../authentication/services/authentication.service";
import { AddFeedbackComponent } from '../feedbacks/add-feedback/add-feedback.component';
import { FeedbacksRepository } from '../feedbacks/repositories/feedbacks.repository';
import { MeetService } from "../meet/meet.service";
import { Organization } from "../organizations/models/organizations.entity";
import { OrganizationStoreService } from "../organizations/services/organization.store.service";
import { DialogService } from '../shared/components/dialog/dialog.service';
import { IconType } from "../shared/components/icon/icon.component";
import { StorageService } from "../shared/storage/storage.service";
import { capitalize } from "../shared/tool-functions/capitalize";
import { talkerCanAccessFormations, talkerCanAccessResources } from "../shared/tool-functions/talker-plan-access";
import { RoleColors } from '../shared/types/role-colors';
import { TypeformService } from "../typeform/typeform.service";
import { Talker } from "../users/models/talker.entity";
import { Role, RoleDescription } from "../users/models/users.entity";
import { Profile, ProfileService } from "../users/services/profile.service";


type TabName = {
  [role in Role]?: string;
} & {
  all: string;
};

type Submenu = {
  id: string,
  name: TabName,
}

type MenuTab = {
  id: string,
  name: TabName,
  icon: IconType,
  isAllowed(profile?: Profile): boolean,
  submenus?: Submenu[],
}

@Component({
  selector: 'app-tabs',
  templateUrl: './tabs.component.html',
  styleUrls: [ './tabs.component.scss' ]
})
export class TabsComponent implements OnInit, OnDestroy {
  profile!: Profile;

  organizationSelected?: Organization;
  openedSubmenuTabId?: string;

  destroy$ = new Subject<void>();

  entering = false;

  rating: number | undefined;

  feedbackDate?: string;

  // private readonly tracker = inject(MatomoTracker);

  protected readonly capitalize = capitalize;

  constructor(private readonly router: Router,
              private readonly profileService: ProfileService,
              private readonly organizationStoreService: OrganizationStoreService,
              private readonly authenticationService: AuthenticationService,
              private readonly storageService: StorageService,
              private readonly titleService: Title,
              private readonly meetService: MeetService,
              private readonly typeformService: TypeformService,
              private readonly dialog: DialogService,
              private readonly feedbacksRepository: FeedbacksRepository,
            ) {
  }

  private notPilotTabsList: MenuTab[] = [
    {
      id: 'home',
      name: { all: 'Accueil' },
      icon: 'house',
      isAllowed: () => true
    },
    {
      id: 'my-space',
      name: { all: 'Mon espace' },
      icon: 'heart',
      isAllowed: (profile: Profile) => profile.role === Role.Talker
    },
    {
      id: 'resources/all',
      name: { all: 'Ressources' },
      icon: 'play',
      isAllowed: (profile: Profile) =>
        profile.role === Role.Sherpa || this.talkerCanAccessResources(profile)
    },
    {
      id: 'resources/admin-resources',
      name: { all: 'Ressources' },
      icon: 'play',
      isAllowed: (profile: Profile) => profile.role === Role.Admin
    },
    {
      id: 'formations',
      name: { all: 'Formations' },
      icon: 'education',
      isAllowed: (profile: Profile) => profile.role === Role.Admin,
      submenus: [
        {
          id: 'formations/admin-formations-list',
          name: { all: 'Formations' }
        },
        {
          id: 'formations/admin-formation-contents-list',
          name: { all: 'Contenus de formation' }
        }
      ]
    },
    {
      id: 'trains',
      name: { all: 'Parcours' },
      icon: 'calendar',
      isAllowed: (profile: Profile) => profile.role === Role.Admin
    },
    {
      id: 'my-trains',
      name: { all: 'Parcours' },
      icon: 'calendar',
      isAllowed: (profile: Profile) => profile.role === Role.Sherpa
    },
    {
      id: 'organizations',
      name: { all: 'Organisations' },
      icon: 'building',
      isAllowed: (profile: Profile) => profile.role === Role.Admin
    },
    {
      id: 'users',
      name: { all: 'Utilisateurs' },
      icon: 'users',
      isAllowed: (profile: Profile) => profile.role === Role.Admin
    },
    {
      id: 'thematics',
      name: { all: 'Thématiques' },
      icon: 'leaves',
      isAllowed: (profile: Profile) => profile.role !== Role.Sherpa
    },
    {
      id: 'thematics/my-thematics',
      name: { all: 'Thématiques' },
      icon: 'leaves',
      isAllowed: (profile: Profile) => profile.role === Role.Sherpa
    },
    {
      id: 'available-trains',
      name: { all: 'Parcours' },
      icon: 'calendar',
      isAllowed: (profile: Profile) => profile.role === Role.Talker
    },
    {
      id: 'formations',
      name: { all: 'Formations' },
      icon: 'education',
      isAllowed: (profile: Profile) => this.talkerCanAccessFormations(profile)
    },
    {
      id: 'statistics',
      name: { all: 'Statistiques' },
      icon: 'chart',
      isAllowed: (profile: Profile) => profile.role !== Role.Talker
    },
    {
      id: 'questions',
      name: { all: 'Contact' },
      icon: 'bubble-smile',
      isAllowed: (profile: Profile) => profile.role !== Role.Admin
    },
  ];

  getPilotTabsList = (): MenuTab[] => ([
    {
      id: `organizations/${ this.organizationSelected!.id }`,
      name: { all: 'Accueil' },
      icon: 'house',
      isAllowed: () => true
    },
    {
      id: `organizations/${ this.organizationSelected!.id }/talkers`,
      name: { all: 'Collaborateurs' },
      icon: 'users',
      isAllowed: () => this.organizationSelected?.capabilities?.collaborators !== false
    },
    {
      id: `organizations/${this.organizationSelected!.id}/statistics`,
      name: { all: 'Statistiques' },
      icon: 'chart',
      isAllowed: () => this.organizationSelected?.capabilities?.statistics === true
    },
    {
      id: `organizations/${ this.organizationSelected!.id }/edit`,
      name: { all: 'Paramètres' },
      icon: 'parameters',
      isAllowed: () => this.organizationSelected?.capabilities?.organization_settings === true
    },
    {
      id: `organizations/${ this.organizationSelected!.id }/units`,
      name: { all: 'Crédits' },
      icon: 'crawn',
      isAllowed: () => true
    },
    {
      id: 'questions',
      name: { all: 'Contact' },
      icon: 'bubble-smile',
      isAllowed: () => true
    },
  ]);

  private talkerCanAccessFormations(profile: Profile): boolean {
    if (profile.role !== Role.Talker) {
      return false;
    }

    return talkerCanAccessFormations(profile as Talker);
  }

  private talkerCanAccessResources(profile: Profile): boolean {
    if (profile.role !== Role.Talker) {
      return false;
    }

    return talkerCanAccessResources(profile as Talker);
  }

  private tabsAllowed: MenuTab[] = [];

  reloadImage$ = new Subject<void>();

  tabSelected!: MenuTab;

  get tabs() {
    return this.tabsAllowed;
  }

  ngOnInit(): void {
    this.titleService.setTitle("We talk");

    this.entering = true;
    this.profileService.loadProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: () => undefined,
      });

    combineLatest([
      this.router.events
      .pipe(filter(event => event instanceof NavigationEnd),
        map(event => event as NavigationEnd),
        map(routeEvent => routeEvent.url),
        startWith(this.router.url),
        map(url => url.split('?')[0]),
        map(url => url.split('/').slice(1).join('/'))
      ),
      this.profileService.getObservable(),
      this.organizationStoreService.getObservable()])
      .pipe(

        takeUntil(this.destroy$)
      )
      .subscribe(([ url, profile, organization ]) => {
        this.organizationSelected = profile?.role === Role.Admin ? undefined : organization;
        this.profile = profile ?? new Talker();
        this.tabsAllowed = this.organizationSelected
          ? this.getPilotTabsList().filter(tab => tab.isAllowed(profile))
          : this.notPilotTabsList.filter(tab => tab.isAllowed(profile));

        this.tabSelected = [ 'admins', 'sherpas', 'talkers' ].includes(url.split('/')[0])
          ? this.tabsAllowed.find(tab => tab.id === 'users') ?? this.tabSelected
          : [ ...this.tabsAllowed ].reverse().find(tab => url.search(tab.id) !== -1) ?? this.tabSelected;
        this.tabSelected ??= this.tabsAllowed[0];
      });

    this.profileService.imageUpdate$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.reloadImage$.next();
    });

    this.feedbacksRepository.getLatestMergedFeedback().pipe(takeUntil(this.destroy$)).subscribe((feedback)=>{
      this.rating = feedback?.rating;
      this.feedbackDate = (new Date(feedback?.createdAt)).toLocaleDateString();
    });

  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  clickOnLink(id: string): void {
    // Check if the id matches a tab directly
    let matchedTab = this.tabsAllowed.find(tab => tab.id === id);

    // If not, check if it matches any submenu
    if (!matchedTab) {
      matchedTab = this.tabsAllowed.find(tab =>
        tab.submenus?.some(submenu => submenu.id === id)
      );
    }

    this.tabSelected = matchedTab ?? this.tabsAllowed[0];

    // this.tracker.trackEvent(TrackerCategory.MENU_TALKER, TrackerAction.CLICK, `${this.tabSelected.id}-menu`);
    (window._mtm || []).push(
      {
        'event': 'clic-menu',
        'element-menu': this.tabSelected.name.all
      }
    );
    this.router.navigate([ ...id.split('/') ]);
  }

  toggleSubmenu(tabId: string): void {
    this.openedSubmenuTabId = this.openedSubmenuTabId === tabId ? undefined : tabId;
  }

  closeSubmenu(tabId: string): void {
    if (this.openedSubmenuTabId === tabId) {
      this.openedSubmenuTabId = undefined;
    }
  }

  isSubmenuOpened(tabId: string): boolean {
    return this.openedSubmenuTabId === tabId;
  }

  isTabSelected(tab: MenuTab): boolean {
    // Check if the tab itself is selected
    if (this.tabSelected.id === tab.id) {
      return true;
    }

    // If tab has submenus, check if the current route matches any submenu
    if (tab.submenus) {
      const currentUrl = this.router.url.split('?')[0].split('/').slice(1).join('/');
      return tab.submenus.some(submenu => currentUrl.includes(submenu.id));
    }

    return false;
  }

  logout(): void {
    if (!this.isImpersonating) {
      this.authenticationService.logout()
        .pipe(tap(() => this.router.navigate([ 'auth', 'login' ])))
        .subscribe(() => this.profileService.logout());
    } else {
      this.authenticationService.endImpersonate()
        .pipe(
          switchMap(() => this.profileService.loadProfile()),
          tap(() => this.router.navigate([ 'home' ])))
        .subscribe();
    }
  }

  goToProfile() {
    this.router.navigate([ 'my-profile' ]);
  }

  openFeedBackDialog() {
    (window._mtm || []).push({
      'event': 'clic-satisfaction',
    });
    this.dialog.open(AddFeedbackComponent, { rating: this.rating }).pipe(takeUntil(this.destroy$)).subscribe((feedback) => {
      if(feedback){
        this.rating = feedback.rating;
        this.feedbackDate = (new Date(feedback?.createdAt)).toLocaleDateString();
      }
    });
  }

  get isImpersonating() {
    return !!this.storageService.getAdminRefreshToken();
  }

  get role(): string {
    return capitalize(RoleDescription[this.profile.role]);
  }

  get isMeeting(): boolean {
    return this.meetService.isMeeting;
  }

  get isRespondingTypeform(): boolean {
    return this.typeformService.isResponding;
  }

  switchProfile() {
    this.router.navigate([ 'switch-profile' ]);
  }

  mapRatingValueToIconType(rating: number): {type: IconType, color: string} {
    switch (rating) {
        case 1: return { type: 'sad-emoji', color: "#ff4f4f" };
        case 2: return { type: 'woozy-emoji', color:"#FC9418" };
        case 3: return { type: 'neutral-emoji', color: "#FCC418" };
        case 4: return { type: 'happy-emoji', color: "#94D82C" };
        case 5: return { type: 'laughy-emoji', color: "#22C997" };
        default: return { type: 'happy-emoji', color: "#94D82C" };
    }
}

getColorByRole(role: Role): RoleColors {
  return RoleColors[role];
}

}
